import React from 'react';
import PropTypes from 'prop-types';
import { Card, ContentCard, Form, Icon, Section, TooltipHelp, SelectHierarchyLabel } from '@weezevent/nacre';
import EditStyles from '../../styles/components/invitations/edit-attendee-card.module.css';
import { useTranslation } from 'react-i18next';
import { Dropdown as SemanticDropdown } from 'semantic-ui-react';
import Styles from '../../styles/components/invitations/seating-card.module.css';

function EditSeating({ edit, onChange, inventory, rate, rateSelection, zonesOptions, seatsOptions }) {
    const { t } = useTranslation();

    if (!edit || (rateSelection.isGA && zonesOptions.length <= 1)) {
        return null;
    }

    return (
        <div className={Styles['seating-container']}>
            <div className={Styles['seating-input']}>
                <Form.Label label={t('partners.invitation.creation.columns.header.zone')} />
                <SemanticDropdown
                    fluid
                    search
                    selection
                    disabled={zonesOptions.length === 1}
                    placeholder={t('partners.invitation.creation.columns.header.zone')}
                    options={zonesOptions}
                    onChange={(e, { value }) => {
                        onChange(inventory, rate, value, null);
                    }}
                    value={rateSelection.zone}
                />
            </div>
            {!rateSelection.isGA && (
                <div className={Styles['seating-input']}>
                    <Form.Label label={t('partners.invitation.creation.columns.header.seat')} />
                    <SemanticDropdown
                        fluid
                        search
                        selection
                        placeholder={t('partners.invitation.creation.columns.header.seat')}
                        options={seatsOptions}
                        onChange={(event, { value }) => {
                            let booking = seatsOptions.find((booking) => booking.key === value);
                            onChange(inventory, rate, rateSelection.zone, booking.booking);
                        }}
                        value={rateSelection.booking?.booking_uuid}
                    />
                </div>
            )}
        </div>
    );
}

function FirstBooking({ displayFirstBooking, edit, onChange, inventory, rate, rateSelection, zonesOptions, seatsOptions, isManual }) {
    const { t } = useTranslation();

    if (!displayFirstBooking || edit || !inventory || !rate || !zonesOptions.length || (rateSelection.isGA && zonesOptions.length <= 1)) {
        return null;
    }

    return (
        <Section>
            <div style={{ marginBottom: '10px' }}>
                <span style={{ fontWeight: '500' }}>
                    {!rateSelection.isGA ? t('partners.invitation.creation.first_booking.title') : t('partners.invitation.creation.first_booking.title_ga')}
                    <TooltipHelp
                        position="bottom"
                        triggerOnHover
                        title={!rateSelection.isGA ? t('partners.invitation.creation.first_booking.tooltip') : t('partners.invitation.creation.first_booking.tooltip_ga')}
                    />
                </span>
            </div>
            <div className={Styles['seating-container']}>
                <div className={Styles['seating-input']}>
                    <Form.Label label={t('partners.invitation.creation.columns.header.zone')} optionalText={t('partners.common.labelOptional')} />
                    <SemanticDropdown
                        fluid
                        search
                        disabled={zonesOptions.length === 1}
                        selection
                        placeholder={t('partners.invitation.creation.columns.header.zone')}
                        options={zonesOptions}
                        onChange={(e, { value }) => {
                            onChange(inventory, rate, value, null);
                        }}
                        value={isManual ? 'custom' : rateSelection.zone}
                    />
                </div>
                {!rateSelection.isGA && (
                    <div className={Styles['seating-input']}>
                        <Form.Label label={t('partners.invitation.creation.columns.header.seat')} optionalText={t('partners.common.labelOptional')} />
                        <SemanticDropdown
                            fluid
                            search
                            selection
                            placeholder={t('partners.invitation.creation.columns.header.seat')}
                            options={seatsOptions}
                            onChange={(event, { value }) => {
                                let booking = seatsOptions.find((booking) => booking.key === value);
                                onChange(inventory, rate, rateSelection.zone, booking.booking);
                            }}
                            value={rateSelection.is_manual ? 'custom' : rateSelection.first_booking?.booking_uuid}
                        />
                    </div>
                )}
            </div>
        </Section>
    );
}

export function SeatingInformationCard({
    state,
    keyRateSelection = null,
    edit = false,
    initialRateSelection = null,
    attendee = null,
    rateSelection,
    inventory = null,
    rate = null,
    partnerId,
    inventories = [],
    ratesAlreadySelected = [],
    displayFirstBooking = false,
    onChange = () => {},
    onRetrieveBookings = () => {},
    rateModel,
    bookingModel,
    onDeleteInventory = () => {},
    isManual = false,
    onSingleSelectionPossible = () => {}
}) {
    const { t } = useTranslation();

    const [querySearch, setQuerySearch] = React.useState('');
    const [queryRatesSearch, setQueryRatesSearch] = React.useState('');

    const handleQuery = React.useCallback((value) => {
        setQuerySearch(value);
    });

    const handleRatesQuery = React.useCallback((value) => {
        setQueryRatesSearch(value);
    });

    let [rates, loadingRates] = rateModel.useApiModel(
        {
            partnerId,
            launch: Boolean(keyRateSelection && keyRateSelection !== 'new_event'),
            query: { inventories: inventory?.uuid, include_unavailable: edit },
            onSuccess: (rates_obj) => {
                if (rates_obj.length === 1 && !edit) {
                    if (inventories.length === 1) {
                        onSingleSelectionPossible(true);
                    }
                    onChange(inventory, rates_obj[0], null, null);
                }
            }
        },
        [partnerId, inventory, keyRateSelection, edit]
    );

    let [bookings, loadingBookings] = bookingModel.useApiModel(
        {
            partnerId,
            launch: Boolean(keyRateSelection && keyRateSelection !== 'new_event' && rate),
            query: { inventories_in: inventory?.uuid, rates: rate?.id },
            onSuccess: (booking) => {
                onRetrieveBookings(booking, inventory, rate, Boolean(booking.length && booking[0]['area_zone']));
            }
        },
        [partnerId, inventory, rate, keyRateSelection]
    );

    function stylizeRateOption(rate) {
        return (
            <div className="events-picker-options">
                <div>
                    <div className="events-picker-options-name">{`${rate.uuid ? '*' : ''} ${rate.resolved_name || rate.name || rate?.rate_template?.name}`}</div>
                    {(rate.group_name || rate.category_name) && (
                        <div className="events-picker-options-date">
                            {rate.group_name || ''}
                            {rate.group_name && rate.category_name ? ' • ' : ''}
                            {rate.category_name || ''}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const ratesOptions = React.useMemo(() => {
        if (!rates || loadingRates) return [];

        return rates
            .filter((r) => r.id === rate?.id || !ratesAlreadySelected.includes(r.id))
            .map((i) => {
                return {
                    key: i.id,
                    text: stylizeRateOption(i),
                    name: i.resolved_name || i.name || i?.rate_template?.name,
                    value: i.id,
                    rate: i
                };
            });
    }, [rates, loadingRates, ratesAlreadySelected]);

    const zonesOptions = React.useMemo(() => {
        if (!bookings || !bookings.length) {
            return [];
        }

        let zones_options = bookings.map((x) => {
            return { key: x.seat_zone || x.area_zone, text: x.seat_zone_name || x.area_zone_name, value: x.seat_zone || x.area_zone };
        });

        const arrayUniqueByKey = [...new Map(zones_options.map((item) => [item['key'], item])).values()];

        if (isManual) {
            arrayUniqueByKey.push({ key: 'custom', text: t('partners.invitation.creation.first_booking.option_custom'), value: 'custom' });
        }

        return arrayUniqueByKey;
    }, [bookings, loadingBookings, isManual, t]);

    const seatsOptions = React.useMemo(() => {
        if (!rateSelection.zone || rateSelection.isGA || !bookings || !bookings.length) {
            return [];
        }

        const bookings_filtered = bookings.filter((booking) => booking.seat_zone === rateSelection.zone || booking.area_zone === rateSelection.zone);

        let booking_options = bookings_filtered.map((x) => {
            return {
                key: x.booking_uuid,
                text: t('partners.invitation.table.seated_seating', { access: x.seat_access, row: x.seat_row, seat: x.seat_seat }),
                value: x.booking_uuid,
                booking: x.object
            };
        });

        if (edit && initialRateSelection && initialRateSelection.zone === rateSelection.zone) {
            booking_options.push({
                key: initialRateSelection.booking.booking_uuid,
                text: t('partners.invitation.table.seated_seating', {
                    access: initialRateSelection.booking.seat_access,
                    row: initialRateSelection.booking.seat_row,
                    seat: initialRateSelection.booking.seat_seat
                }),
                value: initialRateSelection.booking.booking_uuid,
                booking: initialRateSelection.booking.object
            });
        }

        if (isManual) {
            booking_options.push({ key: 'custom', text: t('partners.invitation.creation.first_booking.option_custom'), value: 'custom' });
        }

        return booking_options;
    }, [state, bookings, rateSelection.zone, rateSelection.isGA, loadingBookings, isManual, initialRateSelection, edit, t]);

    const maxAvailableReached = React.useMemo(() => {
        if (!rateSelection.zone || !rateSelection.bookings_for_zone) {
            return false;
        }

        return state.guests.length > rateSelection.bookings_for_zone.length;
    }, [rateSelection, state]);

    function stylizeEventOption(inventory) {
        const { event, name, virtual } = inventory;
        const { start_date, timezone } = event;

        const formattedDate = new Date(start_date).toLocaleDateString([], {
            minute: '2-digit',
            hour: '2-digit',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: timezone
        });

        return (
            <div className="events-picker-options">
                <div>
                    <div className="events-picker-options-name">{name}</div>
                    <div className="events-picker-options-date">
                        {formattedDate} • {`${name} ${virtual ? t('ticket.charts.inventory.virtual_short') : ''}`}
                    </div>
                </div>
            </div>
        );
    }

    const inventoriesOptions = React.useMemo(() => {
        const order = { true: 1, false: 2 };

        if (!inventories) return [];

        return (
            inventories
                .sort((el1, el2) => order[el1.virtual] - order[el2.virtual] || new Date(el1.event.start_date).getTime() - new Date(el2.event.start_date).getTime())
                .map((i) => ({
                    key: i.uuid,
                    text: stylizeEventOption(i),
                    name: i.name,
                    value: i.uuid,
                    inventory: i
                })) ?? []
        );
    }, [inventories]);

    const handleInventoryLabel = React.useCallback(() => {
        return inventoriesOptions.find((o) => inventory?.uuid === o.key)?.name;
    }, [inventoriesOptions, inventory]);

    const handleRateLabel = React.useCallback(
        (rate) => {
            return ratesOptions.find((o) => rate?.key === o.key)?.name;
        },
        [ratesOptions]
    );

    const inventoriesList = React.useMemo(() => {
        const searchValue = querySearch?.trim().toLowerCase() ?? '';

        if (!searchValue) {
            return inventoriesOptions;
        }

        return inventoriesOptions?.filter((ev) => ev.name.toLowerCase().includes(searchValue));
    }, [inventoriesOptions, querySearch]);

    const ratesList = React.useMemo(() => {
        const searchValue = queryRatesSearch?.trim().toLowerCase() ?? '';

        if (!searchValue) {
            return ratesOptions;
        }

        return ratesOptions?.filter((ev) => ev.name.toLowerCase().includes(searchValue));
    }, [ratesOptions, queryRatesSearch]);

    return (
        <Card className={`${EditStyles['container']} ${Styles['seating-card']}`}>
            <Card.Header>
                <div className={EditStyles['trash-button']}>
                    <Card.Title className={EditStyles['title']} title={t('partners.invitation.table.side_menu.seating_information.title')} />
                    {!edit && Object.keys(state.rateSelections).length > 1 && (
                        <div>
                            <Icon name={'trash'} onClick={() => onDeleteInventory(inventory, rate)} />
                        </div>
                    )}
                </div>
            </Card.Header>
            <ContentCard className={Styles['seating-content-card']}>
                <Form>
                    <div className={Styles['seating-container']}>
                        <div className={Styles['seating-input']}>
                            <div className={Styles['selector-container']}>
                                <SelectHierarchyLabel
                                    label={t('partners.invitation.creation.columns.header.inventory')}
                                    widthIndent="10px"
                                    disabled={edit || inventoriesOptions.length === 1}
                                    options={inventoriesOptions}
                                    externalOptions={inventoriesList}
                                    placeholder={
                                        inventories?.length === 0
                                            ? t('partners.invitation.attendees_quickadd.placeholder_no_invitations')
                                            : t('partners.invitation.creation.columns.header.inventory')
                                    }
                                    onChange={(e, { value }) => {
                                        let inventory = inventoriesOptions.find((inv) => inv.value === value).inventory;
                                        onChange(inventory, null, null, null);
                                    }}
                                    onExternalSearch={handleQuery}
                                    searchValue={querySearch}
                                    value={inventory?.uuid}
                                    onLabelSelected={handleInventoryLabel}
                                />
                            </div>
                        </div>
                        {inventory && (
                            <div className={Styles['seating-input']}>
                                <SelectHierarchyLabel
                                    label={t('partners.invitation.creation.columns.header.rate')}
                                    widthIndent="10px"
                                    options={ratesOptions}
                                    externalOptions={ratesList}
                                    disabled={edit || (ratesOptions.length === 1 && rateSelection.rate)}
                                    placeholder={
                                        inventories?.length === 0
                                            ? t('partners.invitation.attendees_quickadd.placeholder_no_invitations')
                                            : t('partners.invitation.creation.columns.header.rate')
                                    }
                                    onChange={(event, { value }) => {
                                        let rate = ratesOptions.find((rate) => rate.value === value).rate;
                                        onChange(inventory, rate, null, null);
                                    }}
                                    onExternalSearch={handleRatesQuery}
                                    searchValue={queryRatesSearch}
                                    value={rate?.id}
                                    onLabelSelected={handleRateLabel}
                                />
                            </div>
                        )}
                    </div>
                    <FirstBooking
                        edit={edit}
                        onChange={onChange}
                        inventory={inventory}
                        rate={rate}
                        rateSelection={rateSelection}
                        zonesOptions={zonesOptions}
                        seatsOptions={seatsOptions}
                        displayFirstBooking={displayFirstBooking}
                        isManual={isManual}
                    />
                    <EditSeating
                        edit={edit}
                        onChange={onChange}
                        inventory={inventory}
                        rate={rate}
                        rateSelection={rateSelection}
                        zonesOptions={zonesOptions}
                        seatsOptions={seatsOptions}
                    />
                    {maxAvailableReached && !isManual && (
                        <Form.FieldErrors style={{ marginBottom: '15px' }} errors={[t('partners.invitation.table.side_menu.attendee.too_much_attendees_event')]} />
                    )}
                </Form>
            </ContentCard>
        </Card>
    );
}
SeatingInformationCard.propTypes = {
    partnerId: PropTypes.string.isRequired,
    rateModel: PropTypes.any.isRequired,
    bookingModel: PropTypes.any.isRequired
};
